import {type ChangeEvent, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import type {QrType} from '../../../common/constants/qrTypes'
import type {QrFieldsState} from '../../../common/utils/qrPayload'

interface QrDataFormProps {
  type: QrType
  fields: QrFieldsState
  onFieldsChange: <T extends QrType>(type: T, patch: Partial<QrFieldsState[T]>) => void
}

type InputChangeEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement>

const Field = ({label, children}: {label: string; children: ReactNode}) => (
  <label className="qrg-field">
    <span className="qrg-label">{label}</span>
    {children}
  </label>
)

const QrDataForm = ({type, fields, onFieldsChange}: QrDataFormProps) => {
  const {t} = useTranslation()
  const tf = (key: string) => t(`qrGenerator.fields.${key}`)

  switch (type) {
    case 'url':
      return (
        <div className="qrg-form">
          <Field label={tf('url')}>
            <input
              type="text"
              className="qrg-input"
              placeholder="https://example.com"
              value={fields.url.value}
              onChange={(e: InputChangeEvent) => onFieldsChange('url', {value: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'text':
      return (
        <div className="qrg-form">
          <Field label={tf('text')}>
            <textarea
              className="qrg-textarea"
              rows={4}
              value={fields.text.value}
              onChange={(e: InputChangeEvent) => onFieldsChange('text', {value: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'phone':
      return (
        <div className="qrg-form">
          <Field label={tf('phone')}>
            <input
              type="tel"
              className="qrg-input"
              placeholder="+1 555 0100"
              value={fields.phone.value}
              onChange={(e: InputChangeEvent) => onFieldsChange('phone', {value: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'email':
      return (
        <div className="qrg-form">
          <Field label={tf('email')}>
            <input
              type="email"
              className="qrg-input"
              placeholder="hello@example.com"
              value={fields.email.email}
              onChange={(e: InputChangeEvent) => onFieldsChange('email', {email: e.target.value})}
            />
          </Field>
          <Field label={tf('subject')}>
            <input
              type="text"
              className="qrg-input"
              value={fields.email.subject}
              onChange={(e: InputChangeEvent) => onFieldsChange('email', {subject: e.target.value})}
            />
          </Field>
          <Field label={tf('message')}>
            <textarea
              className="qrg-textarea"
              rows={3}
              value={fields.email.body}
              onChange={(e: InputChangeEvent) => onFieldsChange('email', {body: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'sms':
      return (
        <div className="qrg-form">
          <Field label={tf('phone')}>
            <input
              type="tel"
              className="qrg-input"
              placeholder="+1 555 0100"
              value={fields.sms.phone}
              onChange={(e: InputChangeEvent) => onFieldsChange('sms', {phone: e.target.value})}
            />
          </Field>
          <Field label={tf('message')}>
            <textarea
              className="qrg-textarea"
              rows={3}
              value={fields.sms.message}
              onChange={(e: InputChangeEvent) => onFieldsChange('sms', {message: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'whatsapp':
      return (
        <div className="qrg-form">
          <Field label={tf('phone')}>
            <input
              type="tel"
              className="qrg-input"
              placeholder="+1 555 0100"
              value={fields.whatsapp.phone}
              onChange={(e: InputChangeEvent) => onFieldsChange('whatsapp', {phone: e.target.value})}
            />
          </Field>
          <Field label={tf('message')}>
            <textarea
              className="qrg-textarea"
              rows={3}
              value={fields.whatsapp.message}
              onChange={(e: InputChangeEvent) => onFieldsChange('whatsapp', {message: e.target.value})}
            />
          </Field>
          <p className="qrg-hint">{t('qrGenerator.fields.whatsappHint')}</p>
        </div>
      )

    case 'wifi':
      return (
        <div className="qrg-form">
          <Field label={tf('wifiSsid')}>
            <input
              type="text"
              className="qrg-input"
              value={fields.wifi.ssid}
              onChange={(e: InputChangeEvent) => onFieldsChange('wifi', {ssid: e.target.value})}
            />
          </Field>
          <Field label={tf('wifiEncryption')}>
            <select
              className="qrg-select"
              value={fields.wifi.encryption}
              onChange={(e) =>
                onFieldsChange('wifi', {encryption: e.target.value as QrFieldsState['wifi']['encryption']})
              }
            >
              <option value="WPA">WPA/WPA2</option>
              <option value="WEP">WEP</option>
              <option value="nopass">{tf('wifiNoPassword')}</option>
            </select>
          </Field>
          {fields.wifi.encryption !== 'nopass' && (
            <Field label={tf('wifiPassword')}>
              <input
                type="text"
                className="qrg-input"
                value={fields.wifi.password}
                onChange={(e: InputChangeEvent) => onFieldsChange('wifi', {password: e.target.value})}
              />
            </Field>
          )}
          <label className="qrg-toggle">
            <input
              type="checkbox"
              checked={fields.wifi.hidden}
              onChange={(e) => onFieldsChange('wifi', {hidden: e.target.checked})}
            />
            {tf('wifiHidden')}
          </label>
        </div>
      )

    case 'vcard':
      return (
        <div className="qrg-form">
          <div className="qrg-field-pair">
            <Field label={tf('firstName')}>
              <input
                type="text"
                className="qrg-input"
                value={fields.vcard.firstName}
                onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {firstName: e.target.value})}
              />
            </Field>
            <Field label={tf('lastName')}>
              <input
                type="text"
                className="qrg-input"
                value={fields.vcard.lastName}
                onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {lastName: e.target.value})}
              />
            </Field>
          </div>
          <Field label={tf('organization')}>
            <input
              type="text"
              className="qrg-input"
              value={fields.vcard.org}
              onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {org: e.target.value})}
            />
          </Field>
          <Field label={tf('phone')}>
            <input
              type="tel"
              className="qrg-input"
              value={fields.vcard.phone}
              onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {phone: e.target.value})}
            />
          </Field>
          <Field label={tf('email')}>
            <input
              type="email"
              className="qrg-input"
              value={fields.vcard.email}
              onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {email: e.target.value})}
            />
          </Field>
          <Field label={tf('website')}>
            <input
              type="text"
              className="qrg-input"
              placeholder="https://example.com"
              value={fields.vcard.website}
              onChange={(e: InputChangeEvent) => onFieldsChange('vcard', {website: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'event':
      return (
        <div className="qrg-form">
          <Field label={tf('eventTitle')}>
            <input
              type="text"
              className="qrg-input"
              value={fields.event.title}
              onChange={(e: InputChangeEvent) => onFieldsChange('event', {title: e.target.value})}
            />
          </Field>
          <div className="qrg-field-pair">
            <Field label={tf('eventStart')}>
              <input
                type="datetime-local"
                className="qrg-input"
                value={fields.event.start}
                onChange={(e: InputChangeEvent) => onFieldsChange('event', {start: e.target.value})}
              />
            </Field>
            <Field label={tf('eventEnd')}>
              <input
                type="datetime-local"
                className="qrg-input"
                value={fields.event.end}
                onChange={(e: InputChangeEvent) => onFieldsChange('event', {end: e.target.value})}
              />
            </Field>
          </div>
          <Field label={tf('eventLocation')}>
            <input
              type="text"
              className="qrg-input"
              value={fields.event.location}
              onChange={(e: InputChangeEvent) => onFieldsChange('event', {location: e.target.value})}
            />
          </Field>
          <Field label={tf('eventDescription')}>
            <textarea
              className="qrg-textarea"
              rows={3}
              value={fields.event.description}
              onChange={(e: InputChangeEvent) => onFieldsChange('event', {description: e.target.value})}
            />
          </Field>
        </div>
      )

    case 'location':
      return (
        <div className="qrg-form">
          <div className="qrg-field-pair">
            <Field label={tf('latitude')}>
              <input
                type="text"
                inputMode="decimal"
                className="qrg-input"
                placeholder="48.1486"
                value={fields.location.lat}
                onChange={(e: InputChangeEvent) => onFieldsChange('location', {lat: e.target.value})}
              />
            </Field>
            <Field label={tf('longitude')}>
              <input
                type="text"
                inputMode="decimal"
                className="qrg-input"
                placeholder="17.1077"
                value={fields.location.lng}
                onChange={(e: InputChangeEvent) => onFieldsChange('location', {lng: e.target.value})}
              />
            </Field>
          </div>
        </div>
      )
  }
}

export default QrDataForm
